import {getCurrentUserLogin} from '@github-ui/client-env'
import {
  clear,
  createExpiryIndexedStore,
  databaseNameFor,
  del,
  delMany,
  entries,
  EXPIRES_AT_INDEX,
  get,
  getUnbatched,
  promisifyRequest,
  set,
  type IndexedDbStore,
} from './indexed-db-store'
import {trackStorage} from './track-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {yieldToMain} from '@github-ui/yield-to-main'
import type {SendAnalyticsEventFunction} from './types'
export type {SendAnalyticsEventFunction} from './types'

/**
 * Validates the *shape* (structure) of a cached value, not its actual contents.
 * `Check` asserts the value matches the expected schema/proto structure; it does
 * not inspect individual field values. For value-level checks, use the
 * caller-provided {@link DataValidator} (`dataValidator`), which runs after this.
 */
export interface Validator {
  Check(value: unknown): boolean
  Code(): string
  Errors(value: unknown): unknown[]
}

/**
 * Optional, caller-provided validator for the *actual data values* of a cached
 * entry (business rules, version gates, etc.) — as opposed to the structural
 * {@link Validator} shape check. `isValid` runs *after* the shape `validator`
 * passes; returning `false` marks the entry as invalid, which evicts it and
 * reports a `DATA_INVALID` event.
 *
 * `name` is a stable, caller-supplied string that uniquely identifies this
 * validator. Persister instances are cached and keyed in part by this `name`,
 * so two callers sharing a db/store/shape-validator but using different data
 * validators get separate instances. Keep `name` stable across calls (define it
 * once at module/route setup) and unique per distinct `isValid` behaviour.
 */
export type DataValidator = {
  name: string
  isValid: (data: unknown) => boolean
}

type Props = {
  validator: Validator
  sendAnalyticsEvent: SendAnalyticsEventFunction
  storeName?: string
  dataValidator?: DataValidator
}

export const SIMPLE_VALIDATOR = {
  Check(value: unknown): boolean {
    return typeof value === 'object' && value !== null
  },
  Code() {
    return 'simple-validator'
  },
  Errors() {
    return []
  },
}

export const CACHE_EVENTS = {
  CACHE_HIT: 'offline_cache.cache_hit',
  CACHE_MISS: 'offline_cache.cache_miss',
  CACHE_EVICT: 'offline_cache.cache_evict',
  CACHE_INVALID: 'offline_cache.stale_structure',
  DATA_INVALID: 'offline_cache.data_invalid',
  CACHE_EXPIRED: 'offline_cache.expired_item',
  CACHE_SET: 'offline_cache.cache_set',
  CACHE_TIMEOUT: 'offline_cache.cache_timeout',
  CACHE_SESSION_DISABLED: 'offline_cache.session_disabled',
  CACHE_READ_ERROR: 'offline_cache.read_error',
  CACHE_EVICT_ERROR: 'offline_cache.evict_error',
  CACHE_WRITE_ERROR: 'offline_cache.write_error',
  CLEANUP_STARTED: 'offline_cache.cleanup_started',
  CLEANUP_COMPLETED: 'offline_cache.cleanup_completed',
  CLEANUP_ERROR: 'offline_cache.cleanup_error',
  CLEANUP_ITEM_ERROR: 'offline_cache.cleanup_item_error',
  CLEANUP_SKIPPED: 'offline_cache.cleanup_skipped',
  LEGACY_DELETE_BLOCKED: 'offline_cache.legacy_delete_blocked',
  WARMUP: 'offline_cache.warmup',
  WARMUP_ERROR: 'offline_cache.warmup_error',
  ENTRIES_READ: 'offline_cache.entries_read',
  ANY: 'offline_cache.*',
  NONE: 'offline_cache.none',
} as const

const INDEXEDDB_READ_TIMEOUT_MS = 500
const INDEXEDDB_WRITE_TIMEOUT_MS = 2000

// Expired entries handled per task. Everything cleanup does per key -- building
// the key array, issuing the delete requests, reporting the eviction -- scales
// with this number inside a single synchronous task, so it caps how long any one
// cleanup task can hold the main thread. A user returning after a long absence
// can have thousands of expired entries at once; the rest are picked up on the
// next pass, after a yield.
export const CLEANUP_BATCH_SIZE = 250

// Passes per cleanup run. Bounds a pathological backlog to a predictable amount
// of work per run, and guarantees termination even if deletes silently no-op.
const CLEANUP_MAX_PASSES = 20

// Key used only to force the lazy connection open during warmup. It is
// never written, so the probe read is always a cheap miss.
const WARMUP_PROBE_KEY = '__offline_cache_warmup_probe__'

// localStorage key prefix recording when the last full-store cleanup scan
// finished, so the scan is not repeated on every page load. Deliberately kept
// out of IndexedDB: it has to stay readable when the database is *gone* --
// deleted, or superseded by a rename -- which is the same reason the schema
// stamp below has no choice, since a record cannot date the arrival of the
// schema it lives in. Keeping the pair together beats splitting them across two
// storage systems. It would also otherwise surface in `entries()` and count
// toward the `total_entries` telemetry.
const LAST_CLEANUP_KEY_PREFIX = 'offline_cache_last_cleanup::'

// Web Locks name prefix for the cross-tab cleanup lock. Lock names are shared by
// the whole origin, so this is namespaced by database and object store to match
// the scope of the work it guards.
export const CLEANUP_LOCK_PREFIX = 'offline_cache_cleanup::'

// localStorage key recording when this browser profile first ran a bundle using
// the current schema. Defined alongside `DBNAME_PREFIX` below, which it derives
// from.

// How long after this browser first ran the current schema before legacy
// databases are deleted. See `deleteLegacyDatabases`.
export const LEGACY_DELETE_GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000

// Read and written directly rather than through `@github-ui/safe-storage`, which
// would pull `@github-ui/stats` into the module graph of every consumer of this
// package — including service workers. Storage access can throw outright when a
// browser blocks it (Safari private mode, blocked cookies), so both are guarded.
function readTimestamp(key: string): number {
  try {
    return Number(globalThis.localStorage?.getItem(key))
  } catch {
    return 0
  }
}

function writeTimestamp(key: string) {
  try {
    globalThis.localStorage?.setItem(key, String(Date.now()))
  } catch {
    // Losing the timestamp only costs a redundant scan on the next page load,
    // or -- for the schema stamp -- never reclaiming the legacy databases.
  }
}

const NOT_AVAILABLE = 'N/A'
let isIndexedDbDisabledForSession = false

function isDOMException(error: unknown): error is DOMException {
  return typeof DOMException !== 'undefined' && error instanceof DOMException
}

function isFatalIndexedDbError(error: unknown): error is DOMException {
  if (!isDOMException(error)) return false

  const errorName = error.name
  return (
    errorName === 'SecurityError' ||
    errorName === 'UnknownError' ||
    errorName === 'AbortError' ||
    errorName === 'QuotaExceededError' ||
    errorName === 'NotFoundError' ||
    errorName === 'VersionError'
  )
}

/**
 * `db.transaction()` throws `InvalidStateError` when the connection is closing
 * or closed — Safari dropping it spontaneously, or `onversionchange` closing it
 * for a newer bundle. Unlike the fatal errors, the condition does not persist:
 * a fresh connection works. But the store caches its open promise, and Safari's
 * `onclose` is not guaranteed to fire, so the dead connection can outlive the
 * event meant to clear it. Recreating the store drops it unconditionally.
 */
function isClosedConnectionError(error: unknown): error is DOMException {
  return isDOMException(error) && error.name === 'InvalidStateError'
}

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof globalThis !== 'undefined' && typeof globalThis.indexedDB !== 'undefined'
  } catch {
    return false
  }
}

class IndexedDbTimeoutError extends Error {
  constructor(operation: 'read' | 'write', key: string, timeoutMs: number) {
    super(`IndexedDB ${operation} operation timed out after ${timeoutMs}ms for key: ${key}`)
    this.name = 'IndexedDbTimeoutError'
  }
}

export type CacheEvent = (typeof CACHE_EVENTS)[keyof typeof CACHE_EVENTS]
export type ANY_CACHE_EVENT = typeof CACHE_EVENTS.ANY
export type NO_CACHE_EVENTS = typeof CACHE_EVENTS.NONE

// The schema is carried by the database *name*, not by an IndexedDB version.
// An object store's indexes can only be created in an upgrade transaction, and
// a versioned open of a database another tab still holds open is blocked — with
// the blocked request parked at the head of that database's connection queue,
// stalling every later open of it. Since the cache is disposable, a new schema
// takes a new name instead, which no other tab can be holding.
export const DBNAME_PREFIX = 'offline_cache_v2::'
// Databases left behind by earlier schemas. Deleted opportunistically so the
// rename doesn't strand storage on users' disks.
export const LEGACY_DBNAME_PREFIXES = ['offline_cache::']
// localStorage key recording when this browser profile first ran a bundle using
// the current schema. Not per-user: it answers "how long since this browser
// stopped using the legacy database", which is a property of the browser, not of
// whoever happens to be signed in.
export const SCHEMA_FIRST_SEEN_KEY = `offline_cache_schema_first_seen::${DBNAME_PREFIX}`
export const CACHE_VERSION = 1
// The *base* name for this user's caches, not a database name: each object store
// gets its own database, so the name that is actually opened appends the store
// (see `databaseNameFor`).
export const getDbName = () => DBNAME_PREFIX + getCurrentUserLogin()

type SafeIndexedDbPersister<TPersistedQuery> = {
  cleanupIntervalId: number | undefined
  isCleanupRunning: boolean
  getItem(key: string, attributes?: {[key: string]: unknown}): Promise<TPersistedQuery | undefined>
  setItem(
    key: string,
    value: TPersistedQuery,
    dataUpdatedAt?: number,
    ttl?: number,
    attributes?: {[key: string]: unknown},
  ): Promise<void>
  removeItem(key: string, attributes?: {[key: string]: unknown}): Promise<void>
  isBustedOrExpired(persistedQuery: TPersistedQuery): boolean
  entries(): Promise<Array<[string, TPersistedQuery]>>
  cleanupExpiredEntries(): Promise<void>
  stopPeriodicCleanup(): void
  warmup(): Promise<void>
}

// Singleton cache to ensure only one persister instance per store
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const persisters = new Map<string, SafeIndexedDbPersister<any>>()

// Cleanup is a whole-store operation, but persisters are keyed by validator, so
// many instances share a single IndexedDB object store. Without this shared
// state each instance would schedule and run its own full-store scan. Keyed by
// `${dbName}::${storeName}`.
//
// `intervalId` doubles as the "is scheduled" flag. Keeping the timer here rather
// than on the persister instance matters: if the flag lived in shared state
// while the timer lived on whichever instance happened to start it, a *different*
// instance calling `stopPeriodicCleanup()` would clear the flag without clearing
// the live timer, and the next `startPeriodicCleanup()` would add a second one.
const cleanupStates = new Map<string, {running: boolean; intervalId: number | undefined}>()

function getCleanupState(cleanupKey: string) {
  let state = cleanupStates.get(cleanupKey)
  if (!state) {
    state = {running: false, intervalId: undefined}
    cleanupStates.set(cleanupKey, state)
  }
  return state
}

export function clearPersisterCache() {
  for (const persister of persisters.values()) {
    if (persister && typeof persister.stopPeriodicCleanup === 'function') {
      persister.stopPeriodicCleanup()
    }
  }
  persisters.clear()
  cleanupStates.clear()
  resetSessionState()
}

export function resetSessionState() {
  isIndexedDbDisabledForSession = false
  hasRequestedLegacyDatabaseDeletion = false
}

let hasRequestedLegacyDatabaseDeletion = false

/**
 * Databases written by an earlier schema are dead weight: this bundle reads and
 * writes `DBNAME_PREFIX` only, so nothing will ever open them again.
 *
 * Held back until this browser has been running the current schema for
 * `LEGACY_DELETE_GRACE_PERIOD_MS`, because bundles predating that rename never
 * close their connection on `versionchange`. A delete issued while one is still
 * alive stays `blocked` and parks at the head of the legacy database's
 * connection queue. That is harmless to transactions on a connection such a tab
 * already holds, and harmless to this bundle (which never opens that database) —
 * but it starves a *reopen*, so a second old tab whose connection dropped
 * (Safari closes them spontaneously) would find its cache stalled.
 *
 * The wait is measured per browser rather than coordinated centrally because
 * that is the question actually being asked: a tab that could still be running
 * the old bundle must predate the moment this browser first ran the new one. A
 * flag flipped globally cannot see that; this stamp can. It also means the
 * migration finishes on its own, with no follow-up operational step to forget.
 *
 * `blocked` is reported rather than handled: an in-flight `IDBOpenDBRequest`
 * cannot be cancelled, so there is nothing to do about it except find out that
 * the grace period was too short.
 */
async function deleteLegacyDatabases(sendAnalyticsEvent: SendAnalyticsEventFunction) {
  if (hasRequestedLegacyDatabaseDeletion) return
  hasRequestedLegacyDatabaseDeletion = true

  const firstSeenAt = readTimestamp(SCHEMA_FIRST_SEEN_KEY)

  // First run under this schema, or the stamp was lost. Start the clock; the
  // legacy databases keep for another interval.
  if (!firstSeenAt || !Number.isFinite(firstSeenAt)) {
    writeTimestamp(SCHEMA_FIRST_SEEN_KEY)
    return
  }

  // A stamp in the future means the clock moved backwards. Re-stamp rather than
  // waiting out a grace period that may never elapse.
  const age = Date.now() - firstSeenAt
  if (age < 0) {
    writeTimestamp(SCHEMA_FIRST_SEEN_KEY)
    return
  }

  if (age < LEGACY_DELETE_GRACE_PERIOD_MS) return

  try {
    const databases = (await globalThis.indexedDB?.databases?.()) ?? []
    for (const {name} of databases) {
      if (name && LEGACY_DBNAME_PREFIXES.some(prefix => name.startsWith(prefix))) {
        const request = globalThis.indexedDB.deleteDatabase(name)
        request.onblocked = () => {
          sendAnalyticsEvent(CACHE_EVENTS.LEGACY_DELETE_BLOCKED, '', {age_ms: age})
        }
      }
    }
  } catch {
    // Enumeration isn't available in every browser or private-browsing mode.
    // Reclaiming the old databases is a nice-to-have, never a correctness need.
  }
}

declare global {
  interface Window {
    __SAFE_INDEXED_DB_CLEAR_PERSISTERS__?: () => void
  }
}

// Expose a global cleanup hook so tests can stop persister periodic-cleanup
// timers and clear the singleton registry without importing this module
// (avoids vi.mock issues). Mirrors `__SAFE_STORAGE_CLEAR_ALL_CACHES__` in
// @github-ui/use-safe-storage.
if (typeof window !== 'undefined') {
  window.__SAFE_INDEXED_DB_CLEAR_PERSISTERS__ = () => {
    clearPersisterCache()
  }
}

export function createSafeIndexedDbPersister<
  TState extends {
    data: unknown | null
    dataUpdatedAt?: number
    ttl?: number
    expiresAt?: number
    preheatSource?: string
    cacheVersion?: number
    variables?: Record<string, unknown>
  },
  TPersistedQuery extends {state: TState; queryKey?: readonly unknown[]; queryHash: string},
>({validator, sendAnalyticsEvent, storeName = 'queries', dataValidator}: Props) {
  // Persisters are cached and shared per id, so the id captures everything baked
  // into the instance: db name, store, shape-validator `Code()`, and the data
  // validator's `name` (so callers with different data validators don't share).
  // The data validator segment is prefixed when present and empty when absent, so
  // a caller can't impersonate the "no validator" case by naming theirs `none`.
  const dataValidatorKey = dataValidator ? `data:${dataValidator.name}` : ''
  const persisterId = `${getDbName()}:${storeName}:${validator.Code()}:${dataValidatorKey}`
  // The cleanup throttle and the cross-tab lock both guard work against one
  // database, so both are keyed by the database this persister actually opens
  // rather than by a string that merely resembles it.
  const cleanupKey = databaseNameFor(getDbName(), storeName)
  const lastCleanupKey = LAST_CLEANUP_KEY_PREFIX + cleanupKey

  // Return existing instance if available
  const existingPersister = persisters.get(persisterId)
  if (existingPersister) {
    return existingPersister
  }

  function isExpired(persistedQuery: TPersistedQuery) {
    // Treat null/undefined records or records without state as expired (should be cleaned up)
    if (!persistedQuery || !persistedQuery.state) {
      return true
    }

    if (persistedQuery.state.dataUpdatedAt && persistedQuery.state.ttl) {
      const queryAge = Date.now() - persistedQuery.state.dataUpdatedAt
      if (queryAge > persistedQuery.state.ttl) {
        return true
      }
    }
    if (persistedQuery.state.cacheVersion !== undefined && persistedQuery.state.cacheVersion !== CACHE_VERSION) {
      return true
    }

    // Treat queries without dataUpdatedAt or ttl as never expiring
    return false
  }

  // `createExpiryIndexedStore` only builds a closure — it defers `indexedDB.open` until
  // the first real read/write (`getDb()` inside the returned function). So this
  // constructor never throws and `idbStore` is always defined.
  let idbStore: IndexedDbStore = createExpiryIndexedStore(getDbName(), storeName)
  function recoverFromClosedConnection() {
    // Deliberately no inline retry after recovery: the replacement store opens
    // lazily on the next operation, and a cache miss is cheap.
    idbStore = createExpiryIndexedStore(getDbName(), storeName)
  }

  // Ensures the connection is only warmed once per persister instance.
  let hasWarmedUp = false

  function isIndexedDbDisabled(): boolean {
    return isFeatureEnabled('disable-indexdb-operations') || isIndexedDbDisabledForSession
  }

  async function withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: 'read' | 'write',
    key: string,
  ): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_TIMEOUT, key, {operation})
        reject(new IndexedDbTimeoutError(operation, key, timeoutMs))
      }, timeoutMs)
    })

    try {
      const result = await Promise.race([promise, timeoutPromise])
      // Clear timeout if promise resolves successfully
      if (timeoutId) clearTimeout(timeoutId)
      return result
    } catch (error) {
      // Clear timeout if promise rejects
      if (timeoutId) clearTimeout(timeoutId)
      throw error
    }
  }

  /**
   * Collects the keys of expired records without deserializing a single cached
   * payload.
   *
   * `getAllKeys` on the expiry index returns the *primary* keys of only the
   * records inside the range, so the browser walks a B-tree of timestamps
   * instead of structured-clone-deserializing every record in the store. The
   * work becomes proportional to the number of expired entries rather than to
   * the size of the cache.
   *
   * Being time-only, this deliberately collects less than `isExpired` rejects:
   * records with a stale `cacheVersion`, and malformed records left by nothing
   * this code can write, have no indexed expiry and so are never swept here.
   * `getItem` evicts them on the next read instead. Collecting them too would
   * mean reading every record back, which is the exact cost the index exists to
   * avoid, and they are already never *served*.
   */
  async function readExpiredKeysFromIndex(objectStore: IDBObjectStore) {
    // Exclusive upper bound: an entry is expired once `now` is strictly past its
    // expiry, matching `isExpired`. Capped at a batch so a huge backlog cannot
    // materialise one huge key array -- the caller drains the rest across yields.
    const expiredKeysRequest = objectStore
      .index(EXPIRES_AT_INDEX)
      .getAllKeys(IDBKeyRange.upperBound(Date.now(), true), CLEANUP_BATCH_SIZE)
    // `count()` reads store metadata rather than records, so `total_entries`
    // telemetry survives at no meaningful cost.
    const totalRequest = objectStore.count()

    // Both requests are issued before awaiting, so the transaction stays active.
    const [expiredKeys, totalEntries] = await Promise.all([
      promisifyRequest(expiredKeysRequest),
      promisifyRequest(totalRequest),
    ])

    return {expiredKeys, totalEntries}
  }

  function findExpiredKeys(store: IndexedDbStore) {
    return store('readonly', readExpiredKeysFromIndex)
  }

  /**
   * Deletes in chunks, yielding between them, so neither the delete requests nor
   * the eviction events form a single task that scales with the backlog.
   */
  async function deleteExpiredKeys(expiredKeys: IDBValidKey[], store: IndexedDbStore) {
    for (let offset = 0; offset < expiredKeys.length; offset += CLEANUP_BATCH_SIZE) {
      const chunk = expiredKeys.slice(offset, offset + CLEANUP_BATCH_SIZE)

      // One transaction for the whole chunk rather than one per expired key.
      await delMany(chunk, store)

      for (const key of chunk) {
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), {
          reason: 'periodic_cleanup',
        })
      }

      if (offset + CLEANUP_BATCH_SIZE < expiredKeys.length) await yieldToMain()
    }
  }

  async function cleanupExpiredEntries() {
    const cleanupState = getCleanupState(cleanupKey)
    if (cleanupState.running) return
    cleanupState.running = true
    const startTime = Date.now()
    try {
      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_STARTED, '', {})

      if (!isIndexedDbAvailable()) {
        return
      }

      let removedCount = 0
      let totalEntries = 0
      let drained = false

      for (let pass = 0; pass < CLEANUP_MAX_PASSES; pass++) {
        const {expiredKeys, totalEntries: storeSize} = await findExpiredKeys(idbStore)
        // Report the size of the store as cleanup found it, not as it shrinks.
        if (pass === 0) totalEntries = storeSize

        if (expiredKeys.length === 0) {
          drained = true
          break
        }

        await deleteExpiredKeys(expiredKeys, idbStore)
        removedCount += expiredKeys.length

        // A short batch means the expired range is exhausted, so there is no
        // point paying for another query to discover that.
        if (expiredKeys.length < CLEANUP_BATCH_SIZE) {
          drained = true
          break
        }

        await yieldToMain()
      }

      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_COMPLETED, '', {
        removed_count: removedCount,
        total_entries: totalEntries,
        duration_ms: Date.now() - startTime,
        drained,
      })

      // Only start the throttle window once the backlog is actually gone.
      // Recording a partial run would lock cleanup out for the full interval
      // with expired entries still on disk.
      if (drained) recordCleanupRun()
    } catch (error) {
      try {
        sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ERROR, '', {
          error: String(error),
        })
      } catch {
        // Swallow analytics errors to prevent unhandled rejections during cleanup
      }
    } finally {
      cleanupState.running = false
    }
  }

  function recordCleanupRun() {
    writeTimestamp(lastCleanupKey)
  }

  /**
   * The full-store scan deserializes every cached payload, which is the most
   * expensive thing this module does on the main thread. It used to run once per
   * page load, so a user navigating around the site paid it over and over even
   * though nothing could have expired in between.
   *
   * When a cleanup already ran within `intervalMs`, the next scan is close to
   * pure waste: entries that expire in the meantime are still evicted lazily on
   * read by `getItem`, so skipping only defers reclaiming disk space until the
   * following pass. The check itself is one small localStorage read, versus
   * deserializing every record in the store.
   */
  function shouldSkipScheduledCleanup(intervalMs: number) {
    const lastCleanupAt = readTimestamp(lastCleanupKey)
    if (!lastCleanupAt || !Number.isFinite(lastCleanupAt)) return false

    // A timestamp in the future means the clock moved backwards; treat it as
    // unknown and scan rather than skipping cleanup indefinitely.
    const age = Date.now() - lastCleanupAt
    if (age < 0 || age >= intervalMs) return false

    sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_SKIPPED, '', {
      reason: 'recently_cleaned',
      age_ms: age,
    })
    return true
  }

  /**
   * Cleanup is scheduled per tab but operates on storage shared by the whole
   * origin, so N open tabs otherwise run N redundant cleanups that compete for
   * the same `readwrite` lock on the object store -- and that store lock is what
   * the data path needs for `getItem`/`setItem`.
   *
   * The localStorage throttle does not prevent this on its own: it is only
   * written once a run drains, so tabs restored together all read a stale
   * timestamp and all proceed.
   *
   * `ifAvailable` makes a tab that finds cleanup already in progress skip this
   * window outright rather than queue behind it. Queueing would just run the
   * whole thing again against a store another tab had already drained.
   */
  async function withCleanupLock(run: () => Promise<void>) {
    // Web Locks requires a secure context, so it is absent on plain http and in
    // some test environments. Running unlocked there preserves prior behaviour:
    // the per-tab guard and the throttle still apply.
    const locks = globalThis.navigator?.locks
    if (!locks) return run()

    await locks.request(CLEANUP_LOCK_PREFIX + cleanupKey, {ifAvailable: true}, async lock => {
      if (!lock) {
        sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_SKIPPED, '', {reason: 'another_tab_cleaning'})
        return
      }
      await run()
    })
  }

  function runScheduledCleanup(intervalMs: number) {
    if (isIndexedDbDisabled() || !isIndexedDbAvailable()) return
    // Cleanup is queued against idle time, so it can come due after the schedule
    // was stopped — during teardown, for instance, or once another persister
    // took over the store. `intervalId` doubles as the "still scheduled" flag.
    if (getCleanupState(cleanupKey).intervalId === undefined) return

    if (getCleanupState(cleanupKey).running) return
    if (shouldSkipScheduledCleanup(intervalMs)) return

    // Behind the throttle on purpose: enumerating databases is the only work a
    // throttled page load would otherwise still pay for, and reclaiming old
    // databases can wait for the same interval everything else does.
    void deleteLegacyDatabases(sendAnalyticsEvent)

    // Only the scheduled path takes the lock. A direct `cleanupExpiredEntries()`
    // call is an explicit request to clean this store now, so it should not be
    // silently skipped because another tab happens to be mid-run.
    void withCleanupLock(cleanupExpiredEntries)
  }

  function startPeriodicCleanup(intervalMs: number = 60 * 60 * 1000) {
    if (typeof window === 'undefined') return

    // Only one persister per (db, store) owns the schedule. Otherwise every
    // validator-specific persister on the page kicks off its own full-store scan.
    const cleanupState = getCleanupState(cleanupKey)
    if (cleanupState.intervalId !== undefined) return

    const scheduleCleanup = () => {
      const windowWithIdleCallback = window as Window & {requestIdleCallback?: typeof requestIdleCallback}
      if (typeof windowWithIdleCallback.requestIdleCallback === 'function') {
        // Deliberately no `timeout` option. A timeout makes the browser run the
        // callback whether or not it is idle, which on a slow startup lands it
        // right where the user is trying to interact. Nothing here is required
        // for correctness — expired entries are still evicted lazily on read by
        // `getItem` — so cleanup should never preempt the user. If the page is
        // busy for its whole life, the next page load or interval tries again.
        windowWithIdleCallback.requestIdleCallback(() => runScheduledCleanup(intervalMs))
      } else {
        // Older Safari has no `requestIdleCallback`. This does run on a busy main
        // thread, which is what makes the per-task batching above load-bearing.
        setTimeout(() => runScheduledCleanup(intervalMs), 10000)
      }
    }

    scheduleCleanup()

    cleanupState.intervalId = window.setInterval(scheduleCleanup, intervalMs)
  }

  function stopPeriodicCleanup() {
    const cleanupState = getCleanupState(cleanupKey)
    if (cleanupState.intervalId !== undefined) {
      clearInterval(cleanupState.intervalId)
      cleanupState.intervalId = undefined
    }
  }

  const persister = {
    // Schedule state is shared by every persister targeting the same object store.
    get cleanupIntervalId() {
      return getCleanupState(cleanupKey).intervalId
    },
    // Cleanup state is shared by every persister targeting the same object store.
    get isCleanupRunning() {
      return getCleanupState(cleanupKey).running
    },
    async getItem(
      key: string,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      if (isIndexedDbDisabled() || !isIndexedDbAvailable()) {
        return
      }

      const startTime = Date.now()
      try {
        const item = await withTimeout(get<TPersistedQuery>(key, idbStore), INDEXEDDB_READ_TIMEOUT_MS, 'read', key)
        const readDurationMs = Date.now() - startTime
        if (!item?.state?.data) {
          // A record that exists but carries no usable data can never be served,
          // and index-based cleanup cannot reach it either: it has no
          // `state.expiresAt`, so it is not in the expiry index. Without evicting
          // it here it would occupy the store forever. Tested against `undefined`
          // rather than for truthiness because that is what distinguishes an
          // absent key, which has nothing to delete, from a stored `null`, which
          // does.
          if (item !== undefined) this.removeItem(key)
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_MISS, key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
          })
          return
        }

        if (isExpired(item)) {
          this.removeItem(key)
          sendAnalyticsEvent?.('offline_cache.expired_item', key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
          })
          return
        }

        // Shape validation: `validator.Check` only asserts the cached value has
        // the expected *structure* (e.g. matches the schema/proto shape). It does
        // not inspect the actual field values.
        //
        // Yield before this synchronous validation so a large cached payload's
        // shape + data validation runs in its own task rather than extending the
        // one that resolved the read. This keeps hydration from forming a single
        // long main-thread block (see INP work on the PR MergeBox/ChecksSection).
        //
        // `yieldToMain` deliberately never waits for idle: this runs on the
        // hydration path, which overlaps app boot, so deferring the continuation
        // would cancel out the "restore sooner" work it exists to enable.
        await yieldToMain()

        const hasValidShape = validator.Check(item.state.data)
        if (!hasValidShape) {
          this.removeItem(key)
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_INVALID, key.toString(), {
            ...attributes,
            errors: validator.Errors(item.state.data),
            read_duration_ms: readDurationMs,
          })
          return
        }

        // Data validation: the caller-provided `dataValidator` inspects the
        // actual data values (business rules, version gates, etc.). It runs only
        // after the shape check passes. A `false` result evicts the entry and
        // reports a distinct event.
        if (dataValidator && !dataValidator.isValid(item.state.data)) {
          this.removeItem(key)
          sendAnalyticsEvent(CACHE_EVENTS.DATA_INVALID, key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
          })
          return
        }

        const age = item.state.dataUpdatedAt ? Date.now() - item.state.dataUpdatedAt : NOT_AVAILABLE
        const ttl = item.state.ttl ?? NOT_AVAILABLE
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_HIT, key.toString(), {
          ...attributes,
          age,
          ttl,
          preheat_source: item.state.preheatSource,
          read_duration_ms: readDurationMs,
        })
        return item
      } catch (error) {
        const readDurationMs = Date.now() - startTime
        if (error instanceof IndexedDbTimeoutError) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_MISS, key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
            timeout: true,
          })
          return undefined
        }
        if (isClosedConnectionError(error)) {
          recoverFromClosedConnection()
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_READ_ERROR, key.toString(), {
            ...attributes,
            session_disabled: false,
            recovered: true,
            error_name: error.name,
            read_duration_ms: readDurationMs,
          })
          return undefined
        }
        if (isFatalIndexedDbError(error)) {
          isIndexedDbDisabledForSession = true
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, key.toString(), {
            reason: 'fatal_error',
            error_name: error.name,
            read_duration_ms: readDurationMs,
          })
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_READ_ERROR, key.toString(), {
            ...attributes,
            session_disabled: true,
            error_name: error.name,
            read_duration_ms: readDurationMs,
          })
          return undefined
        }
        throw error
      }
    },
    async setItem(
      key: string,
      value: TPersistedQuery,
      dataUpdatedAt?: number,
      ttl?: number,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      if (isIndexedDbDisabled() || !isIndexedDbAvailable()) {
        return Promise.resolve()
      }

      const effectiveTtl = ttl ?? value.state.ttl
      const effectiveDataUpdatedAt = dataUpdatedAt ?? value.state.dataUpdatedAt
      const valueToStore = {
        ...value,
        state: {
          ...value.state,
          dataUpdatedAt: effectiveDataUpdatedAt,
          ttl: effectiveTtl,
          cacheVersion: CACHE_VERSION,
          // Absolute expiry, denormalised so cleanup can range-query the index
          // instead of reading each record to recompute it. Left undefined for
          // entries that never expire, which keeps them out of the index.
          expiresAt: effectiveDataUpdatedAt && effectiveTtl ? effectiveDataUpdatedAt + effectiveTtl : undefined,
        },
      }
      trackStorage(key, sendAnalyticsEvent)
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_SET, key.toString(), {
        ...attributes,
        ttl: effectiveTtl,
      })

      try {
        await withTimeout(set(key, valueToStore, idbStore), INDEXEDDB_WRITE_TIMEOUT_MS, 'write', key)
      } catch (error) {
        if (error instanceof IndexedDbTimeoutError) {
          return
        }
        if (isClosedConnectionError(error)) {
          recoverFromClosedConnection()
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_WRITE_ERROR, key.toString(), {
            ...attributes,
            session_disabled: false,
            recovered: true,
            error_name: error.name,
          })
          return
        }
        if (isFatalIndexedDbError(error)) {
          isIndexedDbDisabledForSession = true
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, key.toString(), {
            reason: 'fatal_error',
            error_name: error.name,
          })
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_WRITE_ERROR, key.toString(), {
            ...attributes,
            session_disabled: true,
            error_name: error.name,
          })
          return
        }
        throw error
      }
    },
    async removeItem(key: string, attributes?: {[key: string]: unknown}) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), attributes)

      if (!isIndexedDbAvailable()) {
        return
      }

      return del(key, idbStore)
    },
    isBustedOrExpired(persistedQuery: TPersistedQuery) {
      return isExpired(persistedQuery)
    },
    async entries() {
      if (!isIndexedDbAvailable()) {
        return []
      }

      const startTime = Date.now()
      const things = await entries(idbStore)
      const durationMs = Date.now() - startTime
      sendAnalyticsEvent(CACHE_EVENTS.ENTRIES_READ, '', {
        duration_ms: durationMs,
        entry_count: things.length,
      })
      return things as Array<[string, TPersistedQuery]>
    },
    cleanupExpiredEntries,
    stopPeriodicCleanup,
    // Pre-opens the IndexedDB connection off the critical path (e.g. during idle
    // boot) so the first real getItem() during query resolution only pays the
    // read cost, not the one-time DB-open cost. The store opens the database
    // lazily on first use, so a single cheap probe read is enough to trigger it.
    // Best-effort and idempotent: failures are swallowed and leave the connection
    // to be opened by a later real read.
    async warmup() {
      if (hasWarmedUp) return

      if (isIndexedDbDisabled() || !isIndexedDbAvailable()) {
        return
      }

      hasWarmedUp = true

      const startTime = Date.now()
      try {
        await withTimeout(getUnbatched(WARMUP_PROBE_KEY, idbStore), INDEXEDDB_READ_TIMEOUT_MS, 'read', WARMUP_PROBE_KEY)
        sendAnalyticsEvent(CACHE_EVENTS.WARMUP, '', {duration_ms: Date.now() - startTime})
      } catch (error) {
        const isTimeout = error instanceof IndexedDbTimeoutError

        // Report a dedicated warmup_error for every failure so warmup telemetry is
        // complete and independent. Timeouts additionally emit the generic
        // CACHE_TIMEOUT event via withTimeout; we keep that and attribute the
        // timeout to warmup here via the `timeout` flag.
        sendAnalyticsEvent(CACHE_EVENTS.WARMUP_ERROR, '', {
          error: String(error),
          duration_ms: Date.now() - startTime,
          timeout: isTimeout,
        })

        if (isFatalIndexedDbError(error)) {
          // Preserve the fatal-error session-disable contract used by real reads.
          isIndexedDbDisabledForSession = true
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, '', {
            reason: 'fatal_error',
            error_name: error.name,
          })
          return
        }

        // Allow a later warmup or real read to retry the open.
        hasWarmedUp = false

        // A non-timeout rejection means indexedDB.open itself failed. The store
        // caches the rejected open promise inside its closure, so every later
        // real read would reuse that poisoned promise and reject too. Recreate the
        // store to drop the poisoned connection so subsequent reads can retry.
        // Timeouts leave the in-flight open promise intact (it may still resolve),
        // so the store is preserved in that case.
        if (!isTimeout) {
          recoverFromClosedConnection()
        }
      }
    },
  }

  persisters.set(persisterId, persister)

  startPeriodicCleanup()

  return persister
}

/**
 * A `IndexedDbStore` over a store that already exists in a database this package did
 * not necessarily create, used only when sweeping enumerated databases. Opens
 * without a version, so it can neither block nor be blocked, and closes the
 * connection afterwards so it cannot hold up a later delete.
 */
function openExistingStore(dbName: string, storeName: string): IndexedDbStore {
  return async (txMode, callback) => {
    const db = await promisifyRequest<IDBDatabase>(globalThis.indexedDB.open(dbName))
    try {
      return await callback(db.transaction(storeName, txMode).objectStore(storeName))
    } finally {
      db.close()
    }
  }
}

export async function clearIndexedDBPersisterData() {
  if (!isIndexedDbAvailable()) {
    return
  }

  try {
    const databases = await globalThis.indexedDB?.databases?.()
    if (!databases) return

    const clearPromises = databases
      .filter(db => db.name?.startsWith(DBNAME_PREFIX))
      .map(async db => {
        if (!db.name) return

        try {
          const openRequest = globalThis.indexedDB.open(db.name, db.version)
          const database = await new Promise<IDBDatabase>((resolve, reject) => {
            openRequest.onsuccess = () => resolve(openRequest.result)
            openRequest.onerror = () => reject(openRequest.error)
            openRequest.onupgradeneeded = () => {
              openRequest.transaction?.abort()
              reject(new Error('Unexpected upgrade needed'))
            }
          })

          const storeNames = Array.from(database.objectStoreNames)
          database.close()

          const storeClearPromises = storeNames.map(async storeName => {
            if (!db.name) return
            try {
              // Deliberately not `createExpiryIndexedStore`: these databases are
              // discovered by enumeration and only their existing stores are
              // being emptied, so nothing here should be creating a store -- let
              // alone one carrying this schema's index.
              await clear(openExistingStore(db.name, storeName))
            } catch (error) {
              // Handle fatal errors (like SecurityError) gracefully
              if (isFatalIndexedDbError(error)) {
                return
              }
            }
          })

          await Promise.all(storeClearPromises)
        } catch (error) {
          if (isFatalIndexedDbError(error)) {
            return
          }
        }
      })

    await Promise.allSettled(clearPromises)
  } catch {
    // no-op
  }
}
